import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useState } from 'react';
import Button from '@mui/material/Button';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { Box, Grid, Stack, ThemeProvider } from '@mui/material';
import Slider from '@mui/material/Slider';
import gsap from 'gsap';
import { useEffect } from 'react';
import { generateTheme } from '../themes';
import { RPMText } from './subComponents/RPMText';

// Hardcoded UI strings — language-aware via /en/ URL prefix.
const isEnglish = typeof window !== 'undefined' && window.location.pathname.startsWith('/en/');
const STRINGS = isEnglish ? {
    contactUs:     'Contact us',
    selectCards:   'Choose number of cards',
    pricesExVat:   'Prices exclude VAT',
} : {
    contactUs:     'Kontakta oss',
    selectCards:   'Välj antal kort',
    pricesExVat:   'Alla priser är ex. moms',
};

const PriceSlice = ({ sliceData, index, direction }) => {
    const {
        primary: {
            color_theme,
        },
    } = sliceData;


    const theme = generateTheme(color_theme);
    const [sliderValue, setSliderValue] = useState(0);
    const [price, setPrice] = useState(0);

    const containerRef = React.useRef(null);
    const readmoreRef = React.useRef(null);

    const priceIntervals = sliceData.items

    React.useLayoutEffect(() => {


        if (!containerRef.current || !readmoreRef.current) return;

        gsap.fromTo(
            readmoreRef.current,
            {
                y: -20,
                opacity: 0,
            },
            {
                y: 0,
                opacity: 1,
                ease: "none",

                scrollTrigger: {
                    id: "sliceimage",
                    start: "bottom bottom",

                    trigger: containerRef.current,
                    markers: false,

                },

            }
        );
    }, [containerRef.current, readmoreRef.current]);

    const marks = priceIntervals.map((interval, index) => ({
        value: interval.from,
        label: "",
    }));

    const getByDisplayValue = () => {
        if (sliderValue === 0) return STRINGS.selectCards
        return sliderValue + " st"
    }

    const formatter = new Intl.NumberFormat("sv-SE", {
        style: "currency",
        currency: "SEK",
        minimumFractionDigits: 0,
    })

    const getNumberOfItems = () => {
        const currentInterval = priceIntervals.find((interval) => {
            if (interval.to === null) return interval
            return interval.from <= sliderValue && interval.to >= sliderValue;
        })

        return currentInterval.to === null ? (currentInterval.from + "+") : sliderValue
    }

    const isInRange = (index) => {
        //check if sliderValue is in range of interval at index 
        const interval = priceIntervals[index]

        if (interval.to === null) {
            if (sliderValue >= interval.from) return true
        }

        return interval.from <= sliderValue && interval.to >= sliderValue
    }

    const getCurrentUnitPrice = (prefix = "") => {
        const currentInterval = priceIntervals.find((interval) => {
            if (interval.to === null) return interval
            return interval.from <= sliderValue && interval.to >= sliderValue;
        });

        return currentInterval.price === null ? "" : prefix + formatter.format(currentInterval.price)
    };

    const getDiscount = (prefix = "", suffix = "") => {
        const currentPrice = calculatePrice(sliderValue, true) / sliderValue;

        if (currentPrice === 0) {
            return "  "
        }
        const basePrice = priceIntervals[0].price;

        if (currentPrice === basePrice) {
            return "   "
        }

        return prefix + Math.round(((basePrice - currentPrice) / basePrice) * 100) + "%" + suffix
    }

    function calculatePrice(sliderValue, returnRaw = false) {

        if (sliderValue === 0) return 0

        const discount = sliceData.primary.activate_campaign ? sliceData.primary.campaign_discount : 0

        const selectedInterval = priceIntervals.find(interval =>
            interval.from <= sliderValue && (interval.to === null || interval.to >= sliderValue)
        );

        let returnPrice = 0;

        if (selectedInterval.price === null) {
            returnPrice = 0
        }
        else {

            returnPrice = (selectedInterval
                ?
                ((selectedInterval.price * sliderValue) - (discount * sliderValue))
                :
                0)
        }

        if (returnRaw) {

            return returnPrice > 0 ? returnPrice : 0;
        }
        else {
            if (returnPrice === 0) {
                return STRINGS.contactUs
            }
            else {
                return formatter.format(returnPrice);
            }
        }
    }

    const getDiscountPrice = (price) => {
        const discount = sliceData.primary.activate_campaign ? sliceData.primary.campaign_discount : 0
        return formatter.format(price - discount)
    }

    const getDiscountPriceCampaignTotal = () => {
        const discount = sliceData.primary.activate_campaign ? sliceData.primary.campaign_discount : 0
        return formatter.format(-discount * sliderValue)
    }

    useEffect(() => {
        setPrice(calculatePrice(sliderValue));
    }, [sliderValue]);

    return (
        <ThemeProvider theme={theme}>
            <Stack ref={containerRef} id={"price"} direction="column" spacing={0} sx={{ justifyContent: "space-between", alignItems: "center", backgroundColor: "background.primary" }}>
                <Box sx={{ width: "100%", display: "flex", alignItems: "center" }} >
                    <Grid container

                        direction={"row"}
                        justifyContent="space-between"
                        alignItems="flex-start"
                        sx={{ p: { xs: "2rem", md: "6rem" }, maxWidth: "1200px", mx: "auto" }}

                    >
                        <Grid item xs={12} md={5} lg={6} display="flex" px={0} sx={{ justifyContent: { md: "flex-end", xs: "center" } }}>
                            <Box className="headline" >
                                <RPMText priscmicField={sliceData.primary.title} variant='h2' />

                                {sliceData.primary.activate_campaign &&

                                    <RPMText priscmicField={sliceData.primary.campaign_title} variant='h2' sx={{ background: theme.palette.background.alt, color: theme.palette.primary.contrastText }} />

                                }
                                <RPMText priscmicField={sliceData.primary.price_text_2} variant='p' />

                                {sliceData.primary.activate_campaign &&
                                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", my: 3, p: 2, background: theme.palette.background.altbg, }}>
                                        <RPMText priscmicField={sliceData.primary.campaign_text} variant='p' sx={{ fontStyle: "italic" }} />
                                    </Box>
                                }
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start", my: 3, p: 2, backgroundColor: theme.palette.background.secondaryalt }}>
                                    <RPMText priscmicField={sliceData.primary.price_text_1} variant='p' />

                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6} lg={5} py={4} px={{ xs: 2, md: 4 }} display="flex" sx={{ justifyContent: { md: 'center', sm: 'center', backgroundColor: theme.palette.background.paper, borderRadius: 6 } }}>
                            <Box>
                                <Grid item xs={12} sx={{ display: "flex", justifyContent: "flex-start", width: "100%" }} pb={2}>
                                    <RPMText priscmicField={sliceData.primary.calculate_your_price_header} variant='h4' elementRenderType="h3" sx={{ marginBottom: 0 }} />
                                </Grid>

                                <Grid container
                                    direction={"row"} mt={1}>
                                    <Grid item xs={sliceData.primary.activate_campaign ? 4 : 6} sx={{ display: "flex", justifyContent: "flex-start", width: "100%", background: theme.palette.background.tablebg_dark }} pl={3}>
                                        <RPMText priscmicField={sliceData.primary.price_word_number} variant='h5' elementRenderType="span" sx={{ marginBottom: 0 }} />
                                    </Grid>
                                    <Grid item xs={sliceData.primary.activate_campaign ? 4 : 6} sx={{ display: "flex", justifyContent: "center", width: "100%", background: theme.palette.background.tablebg_dark }}>
                                        <RPMText priscmicField={sliceData.primary.price_word_price_per_card} variant='h5' elementRenderType="span" sx={{ marginBottom: 0 }} />
                                    </Grid>
                                    {sliceData.primary.activate_campaign &&
                                        <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", width: "100%", background: theme.palette.background.tablebg_dark }} pr={3}>
                                            <RPMText priscmicField={sliceData.primary.campaign_word_campaign} variant='h4' elementRenderType="span" sx={{ marginBottom: 0 }} />
                                        </Grid>
                                    }
                                </Grid>

                                {sliceData.items.map((item, index) => (
                                    <Grid container
                                        alignItems={"center"}
                                        my={1.5}
                                        py={1.5}
                                        direction={"row"} key={"ccps" + index} sx={{
                                            background: isInRange(index) ? theme.palette.background.default : "transparent"
                                        }}>
                                        <Grid item xs={sliceData.primary.activate_campaign ? 4 : 6} >
                                            <Typography variant="p" pl={3}>
                                                {item.from} - {item.to}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={sliceData.primary.activate_campaign ? 4 : 6} sx={{ display: "flex", justifyContent: "center", width: "100%", backgroundColor: isInRange(index) ? theme.palette.background.highlight : theme.palette.background.tablebg_light }}>
                                            <Typography variant="p"
                                                sx={{ typography: { textAlign: "left", textDecoration: sliceData.primary.activate_campaign ? "line-through" : "none", color: theme.palette.text.primary }, marginBottom: 0 }}>
                                                {item.price > 0 ? item.price + ":-" : "Offert"}
                                            </Typography>
                                        </Grid>
                                        {sliceData.primary.activate_campaign &&
                                            <Grid item xs={4} sx={{ display: "flex", justifyContent: "flex-end", backgroundColor: isInRange(index) ? theme.palette.background.highlight : theme.palette.background.tablebg_light }}>
                                                <Typography style={{ fontWeight: 900 }} px={3}>{item.price ? getDiscountPrice(item.price) : "Offert"}</Typography>
                                            </Grid>
                                        }
                                    </Grid>
                                ))}

                                <RPMText priscmicField={sliceData.primary.price_text_small} variant='p' />


                                <Grid container
                                    direction={"column"} my={8}>

                                    <Grid item xs={12} p={1}>
                                        <Slider
                                            defaultValue={0}
                                            id="priceslider"
                                            size="large"
                                            aria-label={"price slider"}
                                            min={0}
                                            max={priceIntervals[priceIntervals.length - 1].from}
                                            step={1}

                                            value={sliderValue}
                                            marks={marks}
                                            valueLabelDisplay="on"
                                            valueLabelFormat={getByDisplayValue()}
                                            sx={{
                                                '& .MuiSlider-thumb': {
                                                    height: 28,
                                                    width: 28,
                                                },


                                                '& .MuiSlider-valueLabel': {
                                                    lineHeight: 1.2,
                                                    fontSize: 12,
                                                    padding: 0,
                                                    paddingLeft: 1,
                                                    fontSize: 18,
                                                    color: theme.palette.text.primary,
                                                    left: sliderValue === 0 ? 'calc(-50% + 14px)' : "-50%",
                                                    backgroundColor: "transparent"
                                                },
                                                '& .MuiSlider-rail': {
                                                    height: 4,
                                                    backgroundColor: theme.palette.primary.contrastText2,
                                                    borderRadius: 4,
                                                },
                                                '& .MuiSlider-track': {
                                                    height: 10,
                                                    borderRadius: 4,
                                                },




                                            }}
                                            onChange={(event, newValue) => setSliderValue(newValue)}
                                        />

                                    </Grid>

                                    <Grid item xs={12}>
                                        <Stack direction="row" mt={2} spacing={2} sx={{ opacity: sliderValue > 0 ? 1 : 0, justifyContent: "space-between", alignItems: "center" }}>
                                            <RPMText priscmicField={sliceData.primary.number_of_cards} variant='h4' elementRenderType="span" sx={{ marginBottom: 1 }} />
                                            <Typography variant={"p"}>{getNumberOfItems()} {getCurrentUnitPrice("x ")}</Typography>
                                        </Stack>
                                    </Grid>

                                    {sliceData.primary.activate_campaign &&
                                        <Grid item xs={12}>

                                            <Stack direction="row" spacing={2} sx={{ opacity: sliderValue > 0 ? 1 : 0, justifyContent: "space-between", alignItems: "center", color: theme.palette.primary.contrastText2 }}>
                                                <RPMText priscmicField={sliceData.primary.campaign_word_campaign_discount} variant='h4' elementRenderType="span" sx={{ marginBottom: 0 }} />
                                                <Typography variant={"p"} sx={{ color: theme.palette.primary.contrastText2 }} py={0} px={0}>{getDiscountPriceCampaignTotal(sliderValue)}</Typography>
                                            </Stack>
                                        </Grid>
                                    }

                                    <Grid item xs={12}>
                                        <Stack direction="column" spacing={1} sx={{ opacity: sliderValue > 0 ? 1 : 0, justifyContent: "space-between", alignItems: "center" }}>
                                            <Stack direction="row" spacing={2} sx={{ width: "100%", justifyContent: "space-between", alignItems: "center", color: theme.palette.primary.contrastText2 }}>
                                                <RPMText priscmicField={sliceData.primary.your_price_price_label} variant='h4' elementRenderType="span" sx={{ marginBottom: 0 }} />
                                                <Typography variant={"h4"} sx={{}}>{price}</Typography>
                                            </Stack>
                                            <Stack direction="row" spacing={2} sx={{ minHeight: "36px", width: "100%", justifyContent: "flex-end", alignItems: "center" }}>
                                                <Typography variant={"p"} sx={{}}> {getDiscount("(", " " + sliceData.primary.campaign_word_discount[0].text + " )")}</Typography>
                                            </Stack>
                                        </Stack>


                                    </Grid>
                                </Grid>
                                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", borderRadius: "0px", mb: 0 }}>
                                    <Typography variant={"p"}  >{STRINGS.pricesExVat}</Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid >
                </Box >
                <Button id="readMore" ref={readmoreRef} href={"#contact"} variant='text' sx={{ color: "#000", width: "100%", height: "100%", borderRadius: "0px", textTransform: "none", py: 0, px: 0 }} style={{ marginTop: 0 }} onClick={() => { }}>
                    <Stack direction="column" spacing={1} py={2} style={{ marginTop: "0px" }} sx={{ justifyContent: "space-between", width: "100%", alignItems: "center", backgroundColor: { xs: "transparent", md: "transparent" } }}>
                        {STRINGS.contactUs}
                        <ArrowDownwardIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                    </Stack>
                </Button>
            </Stack >
        </ThemeProvider >
    )


}
export default PriceSlice
